import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useNav } from "../contexts/NavContext";
import { calcolaOreGiornoUtil } from "../utils/contabilizzaPresenze";
import { CheckCircle, Clock, AlertTriangle, TrendingUp, ChevronLeft } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { isTaskOverdue } from "../utils/taskHelpers";
import { esc } from "../utils/cartaIntestata";

const LIVELLI = { 1:"Inesperto", 2:"Poco esperto", 3:"Autonomo", 4:"Esperto", 5:"Molto esperto" };
const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

function StatCard({ emoji, label, valore, colore="#7c3aed", sub="" }) {
  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
      <div style={{fontSize:22,marginBottom:4}}>{emoji}</div>
      <div style={{fontSize:22,fontWeight:800,color:colore}}>{valore}</div>
      <div style={{fontSize:11,fontWeight:700,color:"#374151",marginTop:2}}>{label}</div>
      {sub&&<div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>{sub}</div>}
    </div>
  );
}

export default function SchedaDipendentePage({ dipendente, onBack, onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { navigateFull } = useNav();
  const orgId = effectiveOrgId;
  const [orgInfo, setOrgInfo] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  }, [orgId]);

  const [tasks, setTasks] = useState([]);
  const [noteSpese, setNoteSpese] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [projectsMap, setProjectsMap] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  const uid = dipendente?.nexoraUid || dipendente?.uid || null;

  useEffect(()=>{
    if(!orgId||!uid) return;
    const unsubs = [];
    // Attività assegnate
    unsubs.push(onSnapshot(query(collection(db,"tasks"),where("organizationId","==",orgId),where("assignedTo","==",uid)),
      snap=>setTasks(snap.docs.map(d=>({id:d.id,...d.data()})))));
    // Note spese
    unsubs.push(onSnapshot(query(collection(db,"noteSpese"),where("orgId","==",orgId),where("uid","==",uid)),
      snap=>setNoteSpese(snap.docs.map(d=>({id:d.id,...d.data()})))));
    // Scadenze personali (categoria personale)
    unsubs.push(onSnapshot(query(collection(db,"scadenzario"),where("organizationId","==",orgId),where("tipo","==","personale")),
      snap=>setScadenze(snap.docs.map(d=>({id:d.id,...d.data()})))));
    // Presenze
    unsubs.push(onSnapshot(query(collection(db,"presenze"),where("orgId","==",orgId),where("uid","==",uid)),
      snap=>setPresenze(snap.docs.map(d=>({id:d.id,...d.data()})))));
    // Eventi legati al dipendente (dal feed)
    unsubs.push(onSnapshot(query(collection(db,"eventi"),where("orgId","==",orgId),where("uid","==",uid)),
      snap=>{ 
        const list = snap.docs.map(d=>({id:d.id,...d.data()}));
        list.sort((a,b)=>(b.createdAt?.toMillis?.()??0)-(a.createdAt?.toMillis?.()??0));
        setEventi(list);
      }));
    return ()=>unsubs.forEach(u=>u());
  },[orgId,uid]);

  // Documenti commessa completi — servono solo per aprire la commessa al clic dal
  // pannello "Commesse su cui ha lavorato" (la pagina Commessa richiede l'oggetto intero).
  useEffect(()=>{
    if(!orgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",orgId)), snap=>{
      const mappa = {}; snap.docs.forEach(d=>{ mappa[d.id] = {id:d.id,...d.data()}; });
      setProjectsMap(mappa);
    });
  },[orgId]);

  // Statistiche attività
  const taskStats = useMemo(()=>{
    const aperte = tasks.filter(t=>t.status!=="completata"&&!t.archived);
    const completate = tasks.filter(t=>t.status==="completata");
    const oggi = toLocalDateStr(new Date());
    const inRitardo = aperte.filter(isTaskOverdue); // W452: regola unica
    return { aperte:aperte.length, completate:completate.length, inRitardo:inRitardo.length };
  },[tasks]);

  // Ore lavorate da presenze approvate
  const oreStats = useMemo(()=>{
    const approved = presenze.filter(p=>p.stato==="approvata");
    let totOre = 0;
    approved.forEach(p=>{
      Object.values(p.giorni||{}).forEach(g=>{
        // Calcola ore dai segmenti
        // ore di lavoro dal calcolo unico del cartellino (permessi a ore già tolti)
        totOre += calcolaOreGiornoUtil(g.timbrature||[]).segmenti.filter(x=>!x.speciale).reduce((t,x)=>t+x.minuti,0)/60;
      });
    });
    return { totOre:Math.round(totOre*10)/10, mesiApprovati:approved.length };
  },[presenze]);

  // Commesse su cui ha effettivamente lavorato — dato già presente nelle stesse presenze
  // caricate sopra (giorni[].timbrature[].commessaId), qui solo raggruppato per commessa.
  const commesseLavorate = useMemo(()=>{
    const mappa = {}; // commessaId -> { nome, minuti, ultimaData }
    presenze.forEach(p=>{
      Object.entries(p.giorni||{}).forEach(([giornoNum,g])=>{
        const { segmenti } = calcolaOreGiornoUtil(g?.timbrature||[]);
        segmenti.filter(s=>!s.speciale && s.commessaId).forEach(s=>{
          const dataStr = `${p.anno}-${String(p.mese).padStart(2,"0")}-${String(giornoNum).padStart(2,"0")}`;
          if(!mappa[s.commessaId]) mappa[s.commessaId] = { nome:s.commessaNome||"—", minuti:0, ultimaData:dataStr };
          mappa[s.commessaId].minuti += s.minuti;
          if(dataStr > mappa[s.commessaId].ultimaData) mappa[s.commessaId].ultimaData = dataStr;
        });
      });
    });
    return Object.entries(mappa)
      .map(([id,v])=>({ id, ...v, ore:Math.round(v.minuti/6)/10 }))
      .sort((a,b)=>b.ultimaData.localeCompare(a.ultimaData));
  },[presenze]);

  // Spese totali approvate
  const speseStats = useMemo(()=>{
    const aperte = noteSpese.filter(n=>n.stato==="bozza"||n.stato==="inviata");
    const approvate = noteSpese.filter(n=>n.stato==="approvata"||n.stato==="liquidata");
    return { aperte:aperte.length, approvate:approvate.length };
  },[noteSpese]);

  const TABS = [
    {id:"overview",  label:"📊 Riepilogo"},
    {id:"attivita",  label:`✔️ Attività (${tasks.length})`},
    {id:"presenze",  label:`🕐 Presenze (${presenze.length})`},
    {id:"spese",     label:`💳 Note Spese (${noteSpese.length})`},
    {id:"scadenze",  label:`📅 Scadenze`},
    {id:"timeline",  label:`📡 Timeline`},
  ];

  const livInfo = LIVELLI[parseInt(dipendente?.livelloProfessionalita)];
  const LIVELLI_COL = {1:"#6b7280",2:"#d97706",3:"#2563eb",4:"#7c3aed",5:"#059669"};
  const livCol = LIVELLI_COL[parseInt(dipendente?.livelloProfessionalita)]||"#9ca3af";

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;

    let titoloSezione = "Riepilogo";
    let corpoHtml = "";

    if (activeTab === "attivita") {
      titoloSezione = "Attività";
      const oggi = toLocalDateStr(new Date());
      const righe = tasks.map(t => {
        const inRitardo = isTaskOverdue(t);
        const stato = t.status==="completata"?"Completata":t.status==="annullata"?"Annullata":t.status==="sospesa"?"Sospesa":inRitardo?"In ritardo":"Aperta";
        return `<tr><td>${esc(t.title||"—")}</td><td style="color:#7c3aed">${esc(t.projectName||"—")}</td><td>${t.dueDate?t.dueDate.split("-").reverse().join("/"):"—"}</td><td>${stato}</td></tr>`;
      }).join("") || `<tr><td colspan="4" style="text-align:center;color:#9ca3af;">Nessuna attività</td></tr>`;
      corpoHtml = `<table><thead><tr><th>Attività</th><th>Commessa</th><th>Scadenza</th><th>Stato</th></tr></thead><tbody>${righe}</tbody></table>`;
    } else if (activeTab === "presenze") {
      titoloSezione = "Presenze";
      const STATI = {bozza:"In compilazione",inviata:"Inviata",approvata:"Approvata",respinta:"Respinta"};
      const righe = [...presenze].sort((a,b)=>b.anno-a.anno||b.mese-a.mese).map(p =>
        `<tr><td>${MESI[(p.mese||1)-1]} ${p.anno}</td><td>${STATI[p.stato]||"In compilazione"}</td></tr>`
      ).join("") || `<tr><td colspan="2" style="text-align:center;color:#9ca3af;">Nessun cartellino</td></tr>`;
      corpoHtml = `<table><thead><tr><th>Periodo</th><th>Stato</th></tr></thead><tbody>${righe}</tbody></table>`;
    } else if (activeTab === "spese") {
      titoloSezione = "Note Spese";
      const STATI={bozza:"Bozza",inviata:"Inviata",approvata:"Approvata",respinta:"Respinta",liquidata:"Liquidata"};
      const righe = noteSpese.map(n =>
        `<tr><td>${MESI[(n.mese||1)-1]} ${n.anno}</td><td>${STATI[n.stato]||"Bozza"}</td></tr>`
      ).join("") || `<tr><td colspan="2" style="text-align:center;color:#9ca3af;">Nessuna nota spese</td></tr>`;
      corpoHtml = `<table><thead><tr><th>Periodo</th><th>Stato</th></tr></thead><tbody>${righe}</tbody></table>`;
    } else if (activeTab === "scadenze") {
      titoloSezione = "Scadenze";
      const righe = scadenze.map(s =>
        `<tr><td>${s.voce||s.tipo||"—"}</td><td>${s.dataScadenza?s.dataScadenza.split("-").reverse().join("/"):"—"}</td></tr>`
      ).join("") || `<tr><td colspan="2" style="text-align:center;color:#9ca3af;">Nessuna scadenza personale registrata</td></tr>`;
      corpoHtml = `<table><thead><tr><th>Voce</th><th>Scadenza</th></tr></thead><tbody>${righe}</tbody></table>`;
    } else if (activeTab === "timeline") {
      titoloSezione = "Timeline";
      const righe = eventi.map(ev => {
        const ts = ev.createdAt?.toDate?.();
        return `<tr><td>${ev.titolo||"—"}</td><td>${ts?ts.toLocaleDateString("it-IT")+" "+ts.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"}):"—"}</td></tr>`;
      }).join("") || `<tr><td colspan="2" style="text-align:center;color:#9ca3af;">Nessun evento registrato</td></tr>`;
      corpoHtml = `<table><thead><tr><th>Evento</th><th>Data</th></tr></thead><tbody>${righe}</tbody></table>`;
    } else {
      // Riepilogo: dati personali + KPI + commesse su cui ha lavorato
      const commesseHtml = commesseLavorate.map(c => `<tr><td>${c.nome||"—"}</td><td>${c.ore?c.ore+"h":"—"}</td><td>${c.ultimaData?c.ultimaData.split("-").reverse().join("/"):"—"}</td></tr>`).join("");
      corpoHtml = `
        <div class="kpi-row">
          <div class="kpi"><div class="kpi-n">${taskStats.completate}</div><div class="kpi-l">Completate</div></div>
          <div class="kpi"><div class="kpi-n">${taskStats.aperte}</div><div class="kpi-l">Aperte</div></div>
          <div class="kpi"><div class="kpi-n">${taskStats.inRitardo}</div><div class="kpi-l">In ritardo</div></div>
          <div class="kpi"><div class="kpi-n">${oreStats.totOre}h</div><div class="kpi-l">Ore lavorate</div></div>
        </div>
        <h2>Dati Personali</h2>
        <div class="grid">
          <div class="campo"><div class="lbl">Codice fiscale</div>${dipendente?.codiceFiscale||"—"}</div>
          <div class="campo"><div class="lbl">Data nascita</div>${dipendente?.dataNascita?dipendente.dataNascita.split("-").reverse().join("/"):"—"}</div>
          <div class="campo"><div class="lbl">Data assunzione</div>${dipendente?.dataAssunzione?dipendente.dataAssunzione.split("-").reverse().join("/"):"—"}</div>
          <div class="campo"><div class="lbl">Tipo contratto</div>${dipendente?.tipoContratto||"—"}</div>
          <div class="campo"><div class="lbl">Costo orario</div>${dipendente?.costoOrario?`€${dipendente.costoOrario}/h`:"—"}</div>
          <div class="campo"><div class="lbl">Livello</div>${livInfo?`${dipendente.livelloProfessionalita} - ${livInfo}`:"—"}</div>
        </div>
        ${commesseLavorate.length>0?`
        <h2>Commesse su cui ha lavorato</h2>
        <table><thead><tr><th>Commessa</th><th>Ore</th><th>Ultima data</th></tr></thead><tbody>${commesseHtml}</tbody></table>`:""}`;
    }

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Scheda Dipendente — ${dipendente?.nome} ${dipendente?.cognome} — ${titoloSezione}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:15mm 18mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e1b4b;padding-bottom:12px;margin-bottom:16px;}
      h2{font-size:13px;color:#7c3aed;margin:18px 0 8px;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid #e5e7eb;padding-bottom:4px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;margin-bottom:6px;}
      .campo{font-size:12px;}
      .lbl{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.03em;}
      table{width:100%;border-collapse:collapse;margin-top:6px;}
      th{background:#1e1b4b;color:#fff;padding:6px 8px;font-size:10px;text-align:left;text-transform:uppercase;}
      td{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;}
      .kpi-row{display:flex;gap:10px;margin-top:8px;margin-bottom:6px;}
      .kpi{flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px;text-align:center;}
      .kpi-n{font-size:16px;font-weight:800;color:#1e1b4b;}
      .kpi-l{font-size:9px;color:#6b7280;text-transform:uppercase;margin-top:2px;}
    </style></head><body>
      <div class="hdr">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}</div></div>
        <div style="text-align:right;">
          <div style="font-size:16px;font-weight:800;color:#1e1b4b;">👤 ${dipendente?.nome} ${dipendente?.cognome} — ${titoloSezione}</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px;">${dipendente?.mansione||""} — Generato il ${new Date().toLocaleDateString("it-IT")}</div>
        </div>
      </div>
      ${corpoHtml}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"20px 24px",flexShrink:0}}>
        {onBack && (
          <button onClick={onBack}
            style={{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.2)",borderRadius:7,padding:"5px 12px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif",marginBottom:14}}>
            <ChevronLeft size={13}/> ← Torna a Dipendenti
          </button>
        )}
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:"#fff",flexShrink:0}}>
            {((dipendente?.nome||"?")[0]+(dipendente?.cognome||"?")[0]).toUpperCase()}
          </div>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{fontSize:20,fontWeight:800,color:"#fff"}}>{dipendente?.nome} {dipendente?.cognome}</div>
              <button onClick={stampaPDF}
                style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",border:"1px solid rgba(255,255,255,.3)",borderRadius:7,background:"rgba(255,255,255,.1)",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                🖨️ Stampa PDF · {TABS.find(t=>t.id===activeTab)?.label.replace(/^[^\s]+\s/,"")}
              </button>
            </div>
            <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,.6)"}}>{dipendente?.mansione||"—"}</span>
              {livInfo && <span style={{fontSize:11,fontWeight:700,padding:"1px 8px",borderRadius:8,background:livCol+"33",color:"#fff"}}>{parseInt(dipendente.livelloProfessionalita)} - {livInfo}</span>}
              <span style={{fontSize:11,color:dipendente?.tipo==="dipendente"?"#6ee7b7":"#fde68a",fontWeight:600}}>● {dipendente?.tipo||"dipendente"}</span>
            </div>
          </div>
        </div>
        {/* Tab bar */}
        <div style={{display:"flex",gap:4,marginTop:16,overflowX:"auto"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{padding:"5px 12px",border:"none",borderRadius:6,background:activeTab===t.id?"rgba(255,255,255,.2)":"transparent",color:activeTab===t.id?"#fff":"rgba(255,255,255,.5)",cursor:"pointer",fontSize:11,fontWeight:activeTab===t.id?700:400,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenuto */}
      <div style={{flex:1,overflowY:"auto",padding:20,background:"#f8fafc"}}>

        {/* RIEPILOGO */}
        {activeTab==="overview" && (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:12,marginBottom:20}}>
              <StatCard emoji="✔️" label="Attività completate" valore={taskStats.completate} colore="#059669"/>
              <StatCard emoji="⏳" label="Attività aperte" valore={taskStats.aperte} colore="#7c3aed"/>
              <StatCard emoji="⏰" label="In ritardo" valore={taskStats.inRitardo} colore="#dc2626"/>
              <StatCard emoji="🕐" label="Ore lavorate" valore={`${oreStats.totOre}h`} colore="#2563eb" sub={`${oreStats.mesiApprovati} mesi approvati`}/>
              <StatCard emoji="💳" label="Note spese aperte" valore={speseStats.aperte} colore="#d97706"/>
              <StatCard emoji="✅" label="Note spese approvate" valore={speseStats.approvate} colore="#059669"/>
            </div>

            {/* Dati anagrafici */}
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:12,textTransform:"uppercase",letterSpacing:".05em"}}>Dati personali</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
                {[
                  ["Codice fiscale", dipendente?.codiceFiscale],
                  ["Data nascita", dipendente?.dataNascita?.split("-").reverse().join("/")],
                  ["Data assunzione", dipendente?.dataAssunzione?.split("-").reverse().join("/")],
                  ["Tipo contratto", dipendente?.tipoContratto],
                  ["Costo orario", dipendente?.costoOrario ? `€${dipendente.costoOrario}/h` : null],
                  ["Livello", livInfo ? `${dipendente.livelloProfessionalita} - ${livInfo}` : null],
                ].map(([k,v])=>v?(
                  <div key={k}>
                    <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>{k}</div>
                    <div style={{color:"#374151",fontWeight:600,marginTop:1}}>{v}</div>
                  </div>
                ):null)}
              </div>
            </div>

            {commesseLavorate.length > 0 && (
              <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10,textTransform:"uppercase",letterSpacing:".05em"}}>Commesse su cui ha lavorato</div>
                {commesseLavorate.slice(0,6).map(c=>(
                  <div key={c.id} onClick={()=>projectsMap[c.id] && navigateFull({page:"project", project:projectsMap[c.id]})}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #f3f4f6",cursor:projectsMap[c.id]?"pointer":"default"}}>
                    <span style={{flex:1,fontSize:12,color:"#7c3aed",fontWeight:600}}>{c.nome}</span>
                    <span style={{fontSize:11,color:"#374151",fontWeight:700}}>{c.ore}h</span>
                    <span style={{fontSize:10,color:"#9ca3af"}}>ultima: {new Date(c.ultimaData+"T00:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit",year:"2-digit"})}</span>
                  </div>
                ))}
                {commesseLavorate.length>6 && <div style={{fontSize:10,color:"#9ca3af",marginTop:6,fontStyle:"italic"}}>+{commesseLavorate.length-6} altre commesse</div>}
              </div>
            )}

            {/* Attività recenti */}
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:16}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10,textTransform:"uppercase",letterSpacing:".05em"}}>Ultime attività assegnate</div>
              {tasks.slice(0,5).map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid #f3f4f6"}}>
                  <span style={{fontSize:12}}>{t.status==="completata"?"✅":"⏳"}</span>
                  <span style={{flex:1,fontSize:12,color:"#374151",textDecoration:t.status==="completata"?"line-through":"none",opacity:t.status==="completata"?.6:1}}>{t.title}</span>
                  {t.dueDate&&<span style={{fontSize:10,color:"#9ca3af"}}>{t.dueDate.split("-").reverse().join("/")}</span>}
                </div>
              ))}
              {tasks.length===0&&<div style={{fontSize:12,color:"#9ca3af"}}>Nessuna attività assegnata</div>}
            </div>
          </div>
        )}

        {/* ATTIVITÀ */}
        {activeTab==="attivita" && (
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
            <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
              <thead><tr style={{background:"#1e1b4b"}}>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Attività</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Commessa</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Scadenza</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Stato</th>
              </tr></thead>
              <tbody>
                {tasks.map((t,i)=>{
                  const oggi = toLocalDateStr(new Date());
                  const inRitardo = isTaskOverdue(t);
                  return (
                    <tr key={t.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                      <td style={{padding:"8px 12px",fontWeight:600,color:inRitardo?"#dc2626":"#374151"}}>{t.title}{inRitardo&&" ⚠️"}</td>
                      <td onClick={()=>onNavigate&&t.projectId&&onNavigate({page:"project",projectId:t.projectId,project:{id:t.projectId,name:t.projectName}})}
                        style={{padding:"8px 12px",color:"#7c3aed",fontSize:11,cursor:t.projectId?"pointer":"default",textDecoration:t.projectId?"underline":"none",textDecorationColor:"transparent",transition:"text-decoration-color 0.15s"}}
                        onMouseEnter={e=>t.projectId&&(e.currentTarget.style.textDecorationColor="#7c3aed")}
                        onMouseLeave={e=>e.currentTarget.style.textDecorationColor="transparent"}>
                        {t.projectName||"—"}
                      </td>
                      <td style={{padding:"8px 12px",color:"#6b7280"}}>{t.dueDate||"—"}</td>
                      <td style={{padding:"8px 12px"}}>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6,background:t.status==="completata"?"#d1fae5":inRitardo?"#fee2e2":"#ede9fe",color:t.status==="completata"?"#059669":inRitardo?"#dc2626":"#7c3aed"}}>
                          {t.status==="completata"?"Completata":inRitardo?"In ritardo":"Aperta"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {tasks.length===0&&<tr><td colSpan={4} style={{padding:32,textAlign:"center",color:"#9ca3af"}}>Nessuna attività</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* PRESENZE */}
        {activeTab==="presenze" && (
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
            <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
              <thead><tr style={{background:"#1e1b4b"}}>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Periodo</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Stato</th>
                <th style={{padding:"8px 12px",textAlign:"right",color:"#fff",fontSize:10,fontWeight:700}}>Ore lav.</th>
              </tr></thead>
              <tbody>
                {presenze.sort((a,b)=>b.anno-a.anno||b.mese-a.mese).map((p,i)=>{
                  const STATI = {bozza:{l:"In compilazione",c:"#6b7280"},inviata:{l:"Inviata",c:"#2563eb"},approvata:{l:"Approvata",c:"#059669"},respinta:{l:"Respinta",c:"#dc2626"}};
                  const st = STATI[p.stato]||STATI.bozza;
                  return (
                    <tr key={p.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                      <td style={{padding:"8px 12px",fontWeight:600}}>{MESI[(p.mese||1)-1]} {p.anno}</td>
                      <td style={{padding:"8px 12px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6,background:st.c+"22",color:st.c}}>{st.l}</span></td>
                      <td style={{padding:"8px 12px",textAlign:"right",color:"#374151"}}>—</td>
                    </tr>
                  );
                })}
                {presenze.length===0&&<tr><td colSpan={3} style={{padding:32,textAlign:"center",color:"#9ca3af"}}>Nessun cartellino</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* NOTE SPESE */}
        {activeTab==="spese" && (
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
            <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
              <thead><tr style={{background:"#1e1b4b"}}>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Periodo</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Stato</th>
              </tr></thead>
              <tbody>
                {noteSpese.map((n,i)=>{
                  const STATI={bozza:{l:"Bozza",c:"#6b7280"},inviata:{l:"Inviata",c:"#2563eb"},approvata:{l:"Approvata",c:"#059669"},respinta:{l:"Respinta",c:"#dc2626"},liquidata:{l:"Liquidata",c:"#7c3aed"}};
                  const st=STATI[n.stato]||STATI.bozza;
                  return (
                    <tr key={n.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                      <td style={{padding:"8px 12px",fontWeight:600}}>{MESI[(n.mese||1)-1]} {n.anno}</td>
                      <td style={{padding:"8px 12px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6,background:st.c+"22",color:st.c}}>{st.l}</span></td>
                    </tr>
                  );
                })}
                {noteSpese.length===0&&<tr><td colSpan={2} style={{padding:32,textAlign:"center",color:"#9ca3af"}}>Nessuna nota spese</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* SCADENZE PERSONALI */}
        {activeTab==="scadenze" && (
          <div>
            <div style={{fontSize:12,color:"#6b7280",marginBottom:12}}>Scadenze dalla sezione Scadenzario categoria "Personale" associate a questo dipendente.</div>
            {scadenze.filter(s=>s.dipendente===dipendente?.nome+" "+dipendente?.cognome||s.nome?.includes(dipendente?.cognome)).length===0
              ? <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:32,textAlign:"center",color:"#9ca3af"}}>Nessuna scadenza personale registrata</div>
              : scadenze.map(s=>(
                <div key={s.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:"10px 14px",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700}}>{s.voce||s.tipo}</div>
                    <div style={{fontSize:11,color:"#9ca3af"}}>{s.dataScadenza?.split("-").reverse().join("/")}</div>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* TIMELINE */}
        {activeTab==="timeline" && (
          <div>
            {eventi.length===0
              ? <div style={{textAlign:"center",padding:48,color:"#9ca3af"}}><div style={{fontSize:32,marginBottom:12}}>📡</div>Nessun evento registrato per questo dipendente.</div>
              : <div style={{position:"relative"}}>
                  <div style={{position:"absolute",left:18,top:0,bottom:0,width:2,background:"#e5e7eb"}}/>
                  {eventi.map((ev,i)=>{
                    const ts=ev.createdAt?.toDate?.();
                    const EMOJI={commesse:"📁",attivita:"✔️",clienti:"🏢",noteSpese:"💳",acquisti:"🛒",rapportini:"📋",presenze:"🕐",verbali:"📝",dipendenti:"👷"};
                    return (
                      <div key={ev.id} style={{display:"flex",gap:12,marginBottom:12,position:"relative",zIndex:1}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:"#fff",border:"2px solid #e5e7eb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{EMOJI[ev.categoria]||"📌"}</div>
                        <div style={{flex:1,background:"#fff",border:"1px solid #f3f4f6",borderRadius:8,padding:"8px 12px"}}>
                          <div style={{fontSize:12,fontWeight:700,color:"#111827"}}>{ev.titolo}</div>
                          {ts&&<div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{ts.toLocaleDateString("it-IT")} {ts.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}
      </div>
    </div>
  );
}
